/**
 * Gets an inline CSS color to pass it as style attribute.
 * @param color - The color to get.
 * @returns The inline CSS color.
 */
export function getInlineCssColor(color) {
    if (!color) {
        return undefined;
    }
    const literalValues = ['inherit', 'initial', 'unset'];
    let checkColor = color.toLowerCase();
    if (literalValues.includes(checkColor)) {
        return checkColor;
    }
    else if (checkColor.startsWith('--')) {
        return `var(${color})`;
    }
    else if (checkColor.startsWith('color') || checkColor.startsWith('ace-color')) {
        return `var(--${color})`;
    }
    else if (checkColor.startsWith('#') || checkColor.startsWith('rgb') || color.match(/^var\(--.*\)$/)) {
        return color;
    }
    else {
        return `var(--ace-color-${color})`;
    }
}
/**
 * Checks if a given color should be dark or light.
 * @param backgroundColor - The background color to check.
 * @returns Whether the background color should be dark.
 */
export function shouldBeDarkColor(color) {
    const rgbaToHex = (rgba, forceRemoveAlpha = true) => {
        return ('#' +
            rgba
                .replace(/^rgba?\(|\s+|\)$/g, '') // Get's rgba / rgb string values
                .split(',') // splits them at ","
                .filter((_string, index) => !forceRemoveAlpha || index !== 3)
                .map(string => parseFloat(string)) // Converts them to numbers
                .map((number, index) => (index === 3 ? Math.round(number * 255) : number)) // Converts alpha to 255 number
                .map(number => number.toString(16)) // Converts numbers to hex
                .map(string => (string.length === 1 ? '0' + string : string)) // Adds 0 when length of one number is 1
                .join('')); // Puts the array together to a string
    };
    const hexToRgb = (hex) => {
        let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
            ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16),
            }
            : null;
    };
    const getLuminance = (color) => {
        if (!color.startsWith('#')) {
            color = rgbaToHex(color);
        }
        if (color.length === 4) {
            let expandedHexColor = color
                .replace('#', '')
                .split('')
                .map(function (hex) {
                return hex + hex;
            })
                .join('');
            color = '#' + expandedHexColor;
        }
        let colors = hexToRgb(color);
        if (colors) {
            let components = ['r', 'g', 'b'];
            for (let i in components) {
                let c = components[i];
                colors[c] = colors[c] / 255.0;
                if (colors[c] <= 0.03928) {
                    colors[c] = colors[c] / 12.92;
                }
                else {
                    colors[c] = Math.pow((colors[c] + 0.055) / 1.055, 2.4);
                }
            }
            return 0.2126 * colors.r + 0.7152 * colors.g + 0.0722 * colors.b;
        }
        return 0;
    };
    return getLuminance(color) > 0.179;
}
export function getDefaultAccessibleColorBasedOnBackground(backgroundColor, el, type = 'primary') {
    let checkColor = backgroundColor.toLowerCase();
    if (!checkColor.startsWith('#') && !checkColor.startsWith('rgb')) {
        const style = getComputedStyle(el);
        checkColor = style.getPropertyValue(`--color-` + backgroundColor);
    }
    return shouldBeDarkColor(checkColor) ? `text-neutral--${type}-uni-dark` : `text-neutral--${type}-uni-light`;
}
